import { MxpPrepaymentAccountService, GetPrepaymentAccountResponse, GetPaymentPackagesResponse, PaymentPackage } from '../../../core/services/mxp-prepayment-account.service';
import { Component, OnInit, ViewChild} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { BehaviorSubject, timer } from 'rxjs';
import { first } from 'rxjs/operators';
import * as moment from 'moment';
import { FloatValue, Timestamp } from '@ngx-grpc/well-known-types';

import { AuthService } from '@rezolved/auth';
import { BaseTableParams, Column, QueryData, BaseDataSource, ColumnTypeEnum, BaseTableComponent2 } from '@rezolved/ui';
import { PaymentTypeEnum, MxpPaymentGrpcClient, MakeQRPaymentRequestV3, PaymentStatusEnum, PaymentSubtypeEnum, MakeQRPaymentRequestV4 } from '@rezolved/mxp-proto';

import { MxpSnackBarService } from '@rezolve/core/services/mxp-snackbar.service';

import { PrepaymentTableData, PrepaymentTableService } from './prepayment-table.service';


export interface PaymentsPackageInfo {
  packageId: number;
  payments: number;
  expiryDate: Date;
  price: number;
}

@Component({
  selector: 'mxp-prepayment',
  templateUrl: './prepayment.component.html',
  styleUrls: ['./prepayment.component.scss'],
})

export class PrepaymentComponent implements OnInit{

  //General
  showSpinner = false;
  @ViewChild(BaseTableComponent2) baseTableComponent!: BaseTableComponent2<any, any, any, any>;
  paymentsLeft: number | undefined = 0;
  paymentsLeftExpiryDate: Date | undefined = new Date();

  //Packages table
  displayedColumns: string[] = ['payments', 'expiryDate', 'price', 'action'];
  now = new Date();
  lastDay = new Date(Date.UTC(this.now.getFullYear(), this.now.getMonth() + 1, 0)); // Ensure UTC
  packagesDataSource: PaymentsPackageInfo[]= [];

  //QR 
  qrAmount?: string;
  qrImgUrl?: string;

  //Payments table 
  dataSource: BaseDataSource<PrepaymentTableData>;
  defaultParams: BaseTableParams = {
    searchFilter: {},
    sortDirection: 'desc',
    pageIndex: 0,
    pageSize: 5,
    sortField: 'createdOn',
  };
  tableColumns: Column[] = [
    {
      key: 'timestamp',
      displayName: this.translate.instant('CREATED_ON'),
      type: ColumnTypeEnum.Text,
      sortField: 'createdOn',
      columnWidthPercentage: '10'
    },
    {
      key: 'interactedOnTimeStamp',
      displayName: this.translate.instant('INTERACTED_ON'),
      type: ColumnTypeEnum.Text,
      sortField: 'interactedOn',
      columnWidthPercentage: '8'
    },
    {
      key: 'description',
      displayName: this.translate.instant('DESCRIPTION'),
      type: ColumnTypeEnum.TextWithHover,
      sortField: 'description.keyword',
      columnWidthPercentage: '16'
    },
    {
      key: 'status',
      displayName: this.translate.instant('STATUS'),
      type: ColumnTypeEnum.Text,
      sortField: 'status',
      columnWidthPercentage: '10'
    },
    {
      key: 'total',
      displayName: this.translate.instant('TOTAL'),
      type: ColumnTypeEnum.Text,
      sortField: 'amount',
      columnWidthPercentage: '10'
    }
  ]

  query: BehaviorSubject<QueryData<PaymentStatusEnum[], PaymentTypeEnum, PaymentSubtypeEnum>> = new BehaviorSubject<QueryData<PaymentStatusEnum[], PaymentTypeEnum, PaymentSubtypeEnum>>({
    status: [
      PaymentStatusEnum.NOT_INTERACTED,
      PaymentStatusEnum.INTERIM,
      PaymentStatusEnum.PAYMENT_MADE,
      PaymentStatusEnum.REQUEST_POSTPONED,
      PaymentStatusEnum.REQUEST_REJECTED
    ],
    type: PaymentTypeEnum.ALL,
    subtype: PaymentSubtypeEnum.PREPAYMENT,
    createdOnDate: {
      start: moment.utc('0001-01-01').toDate(),
      end: moment().endOf('day').toDate()
    },
    interactedOnDate: {
      start: moment.utc('0001-01-01').toDate(),
      end: moment().endOf('day').toDate()
    },
    additionalData: []
  });

  pageSizeOptions = [2, 5, 10, 20];
  //---------------------------------------
    
  constructor(
    private authService: AuthService,
    private mxpSnackBarService: MxpSnackBarService,
    private mxpPaymentGrpcClient: MxpPaymentGrpcClient,
    private mxpPrepaymentAccountService: MxpPrepaymentAccountService,
    private translate: TranslateService,
    private prepaymentTableService: PrepaymentTableService)
  {
    this.dataSource = new BaseDataSource<PrepaymentTableData>(this.prepaymentTableService);
  }

  ngOnInit() {
    this.dataSource.loading$.subscribe(isLoading => {
      this.showSpinner = isLoading;
    });
    this.getPrepaymentsInfo();
    this.loadPackages(); 

    
  }

  refresh(): void {
    this.query.next(
      {
        ...this.query.value
      });
    this.getPrepaymentsInfo();
  }

  getPrepaymentsInfo() {
    this.mxpPrepaymentAccountService.getPrepaymentAccount().subscribe((prepaymentAccountInfo: GetPrepaymentAccountResponse) => {
      this.paymentsLeft = prepaymentAccountInfo.paymentsLeft;
      this.paymentsLeftExpiryDate = prepaymentAccountInfo.expiryDate ? new Date(prepaymentAccountInfo.expiryDate) : undefined;
    });
  }

  loadPackages(): void {
    this.mxpPrepaymentAccountService.getPaymentPackages().subscribe({
      next: (response: GetPaymentPackagesResponse) => {
        this.packagesDataSource = (response.paymentPackages ?? []).map((pkg: PaymentPackage, index: number) => ({
          packageId: index + 1, 
          payments: pkg.numberOfPayments ?? 0,
          price: pkg.price ?? 0,
          expiryDate: pkg.expiryDate? new Date(pkg.expiryDate): new Date()
        }));
      },
      error: (err) => {
        console.error('Error loading packages', err);
      }
    });
  }


  onBuy(packageId: number): void {
    this.showSpinner = true;
    this.authService.user$.pipe(first()).subscribe((user) => {
      if (user) {
        const selectedPackage = this.packagesDataSource.find(pkg => pkg.packageId === packageId);
        const request = new MakeQRPaymentRequestV4({
          description: this.translate.instant('PREPAYMENT_QR_DESCRIPTION') + selectedPackage?.price,
          reference: 0,
          extendedReference: undefined,
          expiryDate: selectedPackage?.expiryDate ? Timestamp.fromDate(selectedPackage.expiryDate) : undefined,
          contactInfo: undefined,
          amount: { value: selectedPackage?.price } as FloatValue,
          paymentSubtype: PaymentSubtypeEnum.PREPAYMENT,
          numberOfPrepayments: selectedPackage?.payments
        });
        this.mxpSnackBarService.wrapApiCall('SNACKBAR.MAKE_QR_PAYMENT', this.mxpPaymentGrpcClient.makeQRPaymentV4(request)).subscribe((response) => {
          this.qrAmount = selectedPackage?.price.toString();
          this.qrImgUrl = `data:image/png;base64,${response.success?.qrData?.value}`;

          timer(500).subscribe(() => {
            this.refresh();
          });
        },
          (error) => this.showSpinner = false,
          () => this.showSpinner = false
        )
      }
    });
  }


}
